import React from 'react';
import PropTypes from 'prop-types';

import DraftMessage from '../../correspondances/DraftMessage';

import buildReplyEmailHtml from 'utils/miscellaneous/email/build_reply_email_html';
import {buildReplyDraftFromMessage} from 'utils/miscellaneous/email/build_reply_draft';

const EmailResponseView = (props) => {
  const {communicationTarget, isRegenerating, onApproveAndSendSuccess, onDiscard, onRegenerate, requester, response} = props;
  const relationships = communicationTarget?.relationships || [];
  const [replyEmailData, setReplyEmailData] = React.useState(null);
  const isReplyEmailMode = Boolean(replyEmailData);

  React.useEffect(() => {
    let isMounted = true;

    setReplyEmailData(null);

    const loadReplyEmailData = async () => {
      const nextReplyEmailData = await buildReplyDraftFromMessage({
        message: response?.originalMessage || null,
        relationships,
      });

      if (isMounted) {
        setReplyEmailData(nextReplyEmailData);
      }
    };

    loadReplyEmailData();

    return () => {
      isMounted = false;
    };
  }, [relationships, response?.originalMessage]);

  const defaultMessage = isReplyEmailMode ?
    buildReplyEmailHtml({
      message: response.content,
      replyDraft: replyEmailData,
      showQuotedReply: true,
    }) || '' :
    response.content;
  const defaultEmailData = React.useMemo(() => ({
    subject: replyEmailData?.subject || response.subject,
    message: defaultMessage,
    fromName: [requester?.firstName, requester?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim(),
    fromEmail: requester.email,
    delayUntil: 0,
  }), [defaultMessage, replyEmailData?.subject, requester?.email, requester?.firstName, requester?.lastName, response.subject]);
  const defaultRelationship = relationships.find((relationship) => relationship.relationshipRn === replyEmailData?.relationshipRn) ||
    communicationTarget?.defaultRelationship ||
    null;

  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [emailData, setEmailData] = React.useState(defaultEmailData);
  const [selectedSenderUser, setSelectedSenderUser] = React.useState(requester);
  const [selectedRelationship, setSelectedRelationship] = React.useState(defaultRelationship);
  const recipientEmail = communicationTarget?.getRelationshipEmail?.(selectedRelationship) || '';

  const updateEmailData = (key) => (value) => {
    setEmailData((prevEmailData) => ({
      ...prevEmailData,
      [key]: value,
    }));
  };

  React.useEffect(() => {
    setEmailData(defaultEmailData);
    setSelectedSenderUser(requester);
  }, [defaultEmailData, requester]);

  React.useEffect(() => {
    setSelectedRelationship(defaultRelationship);
  }, [defaultRelationship]);

  React.useEffect(() => {
    if (!selectedSenderUser) {
      return;
    }

    const selectedSenderName = `${selectedSenderUser?.firstName || ''} ${selectedSenderUser?.lastName || ''}`.trim();

    if (
      (emailData.fromName ?? '') !== selectedSenderName ||
      (emailData.fromEmail ?? '') !== (selectedSenderUser.email ?? '')
    ) {
      setSelectedSenderUser(null);
    }
  }, [emailData.fromEmail, emailData.fromName, selectedSenderUser]);

  const getPlainTextFromHtml = (value='', excludeQuotedReply=false) => {
    const container = document.createElement('div');
    container.innerHTML = value;
    if (excludeQuotedReply) {
      container.querySelectorAll('.gmail_quote_container').forEach((node) => {
        node.remove();
      });
    }

    return (container.innerText || container.textContent || '').replace(/\u00A0/g, ' ');
  };

  const handleSenderUserSelect = (senderUser) => {
    setSelectedSenderUser(senderUser);

    if (!senderUser) {
      return;
    }

    setEmailData((prevEmailData) => ({
      ...prevEmailData,
      fromName: `${senderUser?.firstName || ''} ${senderUser?.lastName || ''}`.trim(),
      fromEmail: senderUser.email || '',
    }));
  };

  const isHtmlEmailBody = /<\/?[a-z][\s\S]*>/i.test(emailData.message ?? '');
  const canApproveAndSend = Boolean(recipientEmail) && Boolean(communicationTarget?.sendEmail);

  const handleApproveAndSend = React.useCallback(async () => {
    if (!canApproveAndSend) {
      return;
    }

    setIsSendingEmail(true);

    try {
      const htmlMessage = buildReplyEmailHtml({
        message: emailData.message,
        replyDraft: replyEmailData,
        messageIsHtml: isReplyEmailMode || isHtmlEmailBody,
      });
      const plainMessage = getPlainTextFromHtml(emailData.message, isReplyEmailMode);
      const sendPayload = {
        ...emailData,
        message: plainMessage,
        htmlMessage,
        inReplyTo: replyEmailData?.inReplyTo || undefined,
        references: replyEmailData?.references?.length ? replyEmailData.references : undefined,
      };

      await communicationTarget.sendEmail(sendPayload, selectedRelationship?.relationshipRn);
      await onApproveAndSendSuccess?.();
      onDiscard?.();
    } finally {
      setIsSendingEmail(false);
    }
  }, [
    canApproveAndSend,
    communicationTarget,
    emailData,
    isHtmlEmailBody,
    isReplyEmailMode,
    onApproveAndSendSuccess,
    onDiscard,
    replyEmailData,
    selectedRelationship?.relationshipRn,
  ]);

  const fields = [
    {
      key: 'to',
      type: 'relationshipAutocomplete',
      expression: 'skills.SkillsModule.Email.Fields.To',
      getOptionDetail: communicationTarget.getRelationshipEmail,
      options: relationships,
      selectedRelationship,
      setSelectedRelationship,
      autocompleteSx: {
        '& .MuiAutocomplete-endAdornment': {
          right: 0,
        },
      },
    },
    {
      key: 'fromName',
      type: 'adminAutocomplete',
      expression: 'skills.SkillsModule.Email.Fields.From',
      selectedAdmin: selectedSenderUser,
      selectedDisplayValue: emailData.fromName,
      setSelectedAdmin: handleSenderUserSelect,
      showEmail: true,
    },
    {
      key: 'subject',
      type: 'editable',
      expression: 'skills.SkillsModule.Email.Fields.Subject',
      value: emailData.subject,
      onChange: updateEmailData('subject'),
    },
  ];

  const actions = [
    {
      key: 'approve',
      variant: 'primary',
      expression: 'skills.SkillsModule.Email.Actions.ApproveAndSend',
      disabled: isSendingEmail || !canApproveAndSend,
      onClick: handleApproveAndSend,
    },
    {
      key: 'regenerate',
      variant: 'secondary',
      expression: 'skills.SkillsModule.Email.Actions.Regenerate',
      disabled: isRegenerating,
      onClick: onRegenerate,
    },
    {
      key: 'discard',
      variant: 'tertiary',
      expression: 'skills.SkillsModule.Email.Actions.Discard',
      onClick: onDiscard,
    },
  ];

  return (
    <DraftMessage
      titleExpression='skills.SkillsModule.Email.Draft'
      fields={fields}
      bodyField={{
        value: emailData.message,
        onChange: updateEmailData('message'),
        isHtml: isReplyEmailMode || isHtmlEmailBody,
      }}
      actions={actions}
    />
  );
};

EmailResponseView.propTypes = {
  communicationTarget: PropTypes.object,
  isRegenerating: PropTypes.bool,
  onApproveAndSendSuccess: PropTypes.func,
  onDiscard: PropTypes.func,
  onRegenerate: PropTypes.func,
  requester: PropTypes.object.isRequired,
  response: PropTypes.shape({
    content: PropTypes.string,
    message: PropTypes.string,
    originalMessage: PropTypes.object,
    subject: PropTypes.string,
  }),
};

export default React.memo(EmailResponseView);
